import React, { useState } from 'react';
import { Badge, Button, Card, Col, Row, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import useManageDrugs from '../../hooks/patientDashboard/useMangeDrugs';
import useProfileStore from '../../store/patient/profileStore';
import { Trash2Icon } from 'lucide-react';

const DrugCartModal = ({ show, setShow, cartItems, setCartItems }) => {
    const { profile } = useProfileStore();
    const {
        handlePrescribeFill,
        handleBulkPayment,
        handleFetchAddresses,
        handleSaveAddress,
    } = useManageDrugs();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [step, setStep] = useState(1); // Track the current step
    // Set right after the order is created: shows the pay-now panel.
    // Skipping it (or closing) leaves the order as facility payment.
    const [placedOrder, setPlacedOrder] = useState(null);
    const [note, setNote] = useState("");
    const [deliveryMethod, setDeliveryMethod] = useState("");
    const [addresses, setAddresses] = useState([]);
    const [loadingAddresses, setLoadingAddresses] = useState(false);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [showNewAddressForm, setShowNewAddressForm] = useState(false);
    const [savingAddress, setSavingAddress] = useState(false);
    const [newAddress, setNewAddress] = useState({
        label: "Home",
        street: "",
        house_number: "",
        landmark: "",
        phone_number: "",
        recipient_name: "",
        zone_id: "",
        save_to_profile: true,
    });
    const [recipientName, setRecipientName] = useState("");
    const [recipientPhone, setRecipientPhone] = useState("");
    const [deliveryNote, setDeliveryNote] = useState("");

    const handleClose = () => {
        setShow(false);
        setPlacedOrder(null);
        setStep(1);
    };

    const resetAfterOrder = () => {
        setPlacedOrder(null);
        setShow(false);
        setStep(1);
    };

    const removeItem = (id) => {
        setCartItems((prev) => prev.filter((item) => item.id !== id));
    };

    const decreaseQuantity = (id) => {
        setCartItems((prev) => prev.map((item) =>
            item.id === id && item.requestQuantity > 1
                ? { ...item, requestQuantity: item.requestQuantity - 1 }
                : item
        ));
    };

    // Calculate total price based on quantity * costPrice
    const totalPrice = cartItems.reduce((sum, item) => {
        const cost = item?.batches[0]?.costPrice || 0;
        const quantity = item?.requestQuantity || 1;
        return sum + (cost * quantity);
    }, 0);
    const subtotal = totalPrice / 1.075;
    const tax = totalPrice * (7.5 / 107.5);

    const selectedAddress = addresses.find((a) => a.id === selectedAddressId);

    React.useEffect(() => {
        if (step === 3 && deliveryMethod === 'home' && !loadingAddresses) {
            setLoadingAddresses(true);
            handleFetchAddresses()
                .then((data) => setAddresses(data || []))
                .catch(() => setAddresses([]))
                .finally(() => setLoadingAddresses(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, deliveryMethod]);

    const pickAddress = (address) => {
        setSelectedAddressId(address.id);
        setShowNewAddressForm(false);
        if (!recipientName) setRecipientName(address.recipient_name || "");
        if (!recipientPhone) setRecipientPhone(address.phone_number || "");
    };

    const handleSaveNewAddress = async () => {
        if (!newAddress.street.trim()) {
            toast.error("Street address is required");
            return;
        }
        setSavingAddress(true);
        try {
            if (newAddress.save_to_profile) {
                const result = await handleSaveAddress({
                    label: newAddress.label,
                    street: newAddress.street.trim(),
                    house_number: newAddress.house_number.trim() || null,
                    landmark: newAddress.landmark.trim() || null,
                    phone_number: newAddress.phone_number.trim() || null,
                    recipient_name: newAddress.recipient_name.trim() || null,
                    zone_id: newAddress.zone_id ? Number(newAddress.zone_id) : null,
                    is_default: false,
                });
                if (!result?.success) {
                    toast.error(result?.message || "Could not save address");
                    setSavingAddress(false);
                    return;
                }
                pickAddress(result.data);
                toast.success("Address saved to your profile");
            } else {
                setSelectedAddressId(`unsaved-${Date.now()}`);
                setShowNewAddressForm(false);
            }
            if (!recipientName) setRecipientName(newAddress.recipient_name.trim());
            if (!recipientPhone) setRecipientPhone(newAddress.phone_number.trim());
            setSavingAddress(false);
        } catch (err) {
            console.log(err);
            toast.error("Failed to save address");
            setSavingAddress(false);
        }
    };

    const buildDeliveryPayload = () => {
        if (deliveryMethod !== 'home') return { delivery_method: 'pickup' };
        return {
            delivery_method: 'delivery',
            delivery_address_id: selectedAddress &&
                !String(selectedAddress.id).startsWith('unsaved-')
                ? selectedAddress.id
                : null,
            delivery_zone_id: selectedAddress?.zone?.id || selectedAddress?.zone_id || null,
            delivery_address: selectedAddress
                ? [
                    selectedAddress.house_number,
                    selectedAddress.street,
                    selectedAddress.landmark ? `(${selectedAddress.landmark})` : null,
                ]
                    .filter(Boolean)
                    .join(' ')
                : [
                    newAddress.house_number.trim(),
                    newAddress.street.trim(),
                    newAddress.landmark.trim() ? `(${newAddress.landmark.trim()})` : null,
                ]
                    .filter(Boolean)
                    .join(' '),
            delivery_phone: recipientPhone || null,
            delivery_recipient: recipientName || null,
            delivery_note: deliveryNote || null,
        };
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const items = cartItems.map(item => ({
                drug: { id: item.id },
                generic: item.generic ? { id: item.generic.id } : null,
                requestQuantity: item.requestQuantity,
                dose_quantity: String(item.requestQuantity),
            }));

            const response = await handlePrescribeFill(profile?.id, items, note, buildDeliveryPayload());

            if (response.status === 201 || response.status === 200) {
                const data = response.data;
                const transactions = data?.transactions || [];
                // Amount owed after HMO cover — fully-covered orders have a 0 total
                // and can't be charged online.
                const amountDue = transactions.reduce((sum, tx) => sum + Math.abs(tx?.amount || 0), 0);
                setCartItems([]);

                if (amountDue > 0) {
                    // Show the pay-now panel; skipping it keeps facility payment.
                    // Reset submitting so the panel's action buttons are enabled.
                    setSubmitting(false);
                    setPlacedOrder({ transactions, amountDue });
                    return;
                }

                resetAfterOrder();
                toast.success('Order placed — no payment required (fully covered).');
                navigate("/page/purchased-drugs");
            } else {
                console.error("An unknown error occurred");
            }
        } catch (error) {
            console.log(error);
            toast.error('Failed to place order. Please try again.');
        }
        setSubmitting(false);
    };

    const handlePayNow = async () => {
        if (!placedOrder) return;
        setSubmitting(true);
        try {
            const transactionIds = placedOrder.transactions.map(tx => tx.id);
            const payResponse = await handleBulkPayment(transactionIds, profile?.id);
            const authorizationUrl = payResponse?.data?.data?.authorization_url;
            if (authorizationUrl) {
                window.location.href = authorizationUrl;
                return;
            }
            toast.error('Order placed, but we could not start online payment. You can pay it from your Bills page.');
            resetAfterOrder();
            navigate("/page/purchased-drugs");
        } catch (error) {
            console.log(error);
            toast.error('Could not start online payment. You can pay it from your Bills page.');
            resetAfterOrder();
            navigate("/page/purchased-drugs");
        }
        setSubmitting(false);
    };

    const payAtFacility = () => {
        resetAfterOrder();
        toast.success('Order placed successfully — payment due at the facility.');
        navigate("/page/purchased-drugs");
    };

    return (
        <div show={show} onHide={handleClose} centered>
            <div closeButton>
                    <div style={{ fontSize: '18px', fontWeight: '600', }}>
                        {placedOrder
                            ? "Order Placed"
                            : step === 1
                                ? "Selected Drugs"
                                : step === 2
                                    ? "Cart Summary"
                                    : "Delivery Method"}
                    </div>
                </div>
                <div>
                    {placedOrder ? (
                        <div className="text-center py-4">
                            <h5 className="mb-2">Your order has been placed</h5>
                            <p className="fs-sm text-secondary mb-1">
                                Amount due: <strong>₦{Number(placedOrder.amountDue).toLocaleString()}</strong>
                            </p>
                            <p className="fs-sm text-secondary">
                                Complete your online payment now to confirm your order, or skip to pay at the facility.
                            </p>
                        </div>
                    ) : (
                        <>
                        {step === 1 && (
                        <Row lg={1} md={1} sm={1} xl={1} style={{ maxHeight: '60vh', overflowY: "scroll" }}>
                            {cartItems.length === 0 ? (
                                <h5>No drugs in cart</h5>
                            ) : (
                                cartItems.map((item) => (
                                    <Col className='mb-2' key={item.id}>
                                        <Card className="card-event">
                                            <Card.Body className='d-flex align-items-center gap-2 p-2'>
                                                <div className="avatar">
                                                    <span className="avatar-initial fs-20 bg-primary">
                                                        <i className="ri-shopping-cart-line"></i>
                                                    </span>
                                                </div>
                                                <div className='d-flex flex-column '>
                                                    <h6>
                                                        <Link to="" className='fs-sm'>{item.name}</Link>
                                                    </h6>
                                                    {/* <p className="text-secondary fs-sm m-0">Dr. {item?.createdBy}</p> */}
                                                    <div className='fs-sm'>
                                                        ₦{(item?.batches[0]?.costPrice * item.requestQuantity).toLocaleString()}
                                                    </div>
                                                </div>
                                            </Card.Body>
                                            <div className="divider divider-start fw-medium m-0"></div>
                                            <Card.Footer className='d-flex justify-content-between p-2'>

                                                <br />
                                                <div className=' d-flex align-items-center gap-2'>
                                                    <Button
                                                        variant="outline-secondary"
                                                        onClick={() => decreaseQuantity(item.id)}
                                                        disabled={item.requestQuantity <= 1}
                                                    >
                                                        -
                                                    </Button>
                                                    <span className="mx-2">{item.requestQuantity}</span>
                                                    <Button
                                                        variant="outline-secondary"
                                                        onClick={() => setCartItems(prev => prev.map(i => i.id === item.id ? { ...i, requestQuantity: i.requestQuantity + 1 } : i))}
                                                    >
                                                        +
                                                    </Button>
                                                </div>
                                                <Badge bg='none' onClick={() => removeItem(item.id)} style={{ cursor: "pointer" }} className=' text-danger'>
                                                    <Trash2Icon />
                                                </Badge>
                                            </Card.Footer>
                                        </Card>
                                    </Col>
                                ))
                            )}

                            {cartItems.length > 0 && (

                                <div style={{ padding: '10px' }}>
                                    <Form.Label htmlFor="exampleFormControlTextarea1">Add Note</Form.Label>

                                    <Form.Control
                                        as="textarea"
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        id="exampleFormControlTextarea1"
                                        rows="3"
                                        placeholder="Enter Reason here..."
                                    />
                                    {step === 1 && (
                                        <Button variant="primary" className=' mt-2 w-100' onClick={() => setStep(2)} disabled={cartItems.length === 0}>
                                            Continue
                                        </Button>
                                    )}
                                </div>
                            )}
                        </Row>
                    )}

                    {step === 2 && (
                        <div className=' mt-5'>
                            {/* <h5>Summary of your order:</h5> */}
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Drug Name</th>
                                        <th>Quantity</th>
                                        <th>Price (₦)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cartItems.map((item, index) => (
                                        <tr key={item.id}>
                                            <td>{index + 1}</td>
                                            <td>{item.name}</td>
                                            <td>{item.requestQuantity}</td>
                                            <td>{(item?.batches[0]?.costPrice * item.requestQuantity).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="3" className="text-end"><strong>Sub Total (excl. VAT):</strong></td>
                                        <td><strong>₦{subtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong></td>
                                    </tr>
                                    <tr>
                                        <td colSpan="3" className="text-end"><strong>Tax (7.5% incl.):</strong></td>
                                        <td><strong>₦{tax.toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong></td>
                                    </tr>
                                    <tr>
                                        <td colSpan="3" className="text-end"><strong>Total:</strong></td>
                                        <td><strong>₦{totalPrice.toLocaleString()}</strong></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}

                    {step === 3 && (
                        <Form className="mt-4">
                            <Form.Group className="mb-3">
                                <Form.Label>Select Delivery Method</Form.Label>
                                <Form.Select
                                    value={deliveryMethod}
                                    onChange={(e) => setDeliveryMethod(e.target.value)}
                                >
                                    <option value="">-- Select --</option>
                                    <option value="home">Home Delivery</option>
                                    <option value="facility">Collect at Facility</option>
                                </Form.Select>
                            </Form.Group>

                            {deliveryMethod === "home" && (
                                <>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Form.Label className="m-0">Deliver To</Form.Label>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0"
                                            onClick={() => setShowNewAddressForm((v) => !v)}
                                        >
                                            {showNewAddressForm ? "Cancel" : "+ Add New Address"}
                                        </Button>
                                    </div>

                                    {loadingAddresses ? (
                                        <p className="text-secondary fs-sm">Loading saved addresses...</p>
                                    ) : (
                                        <div style={{ maxHeight: '200px', overflowY: 'auto' }} className="mb-3">
                                            {addresses.length === 0 && !showNewAddressForm && (
                                                <p className="text-secondary fs-sm">
                                                    No saved addresses yet. Add one below.
                                                </p>
                                            )}
                                            {addresses.map((address) => (
                                                <Card
                                                    key={address.id}
                                                    onClick={() => pickAddress(address)}
                                                    style={{ cursor: 'pointer' }}
                                                    className={`mb-2 ${selectedAddressId === address.id ? 'border-primary' : ''}`}
                                                >
                                                    <Card.Body className="p-2 d-flex align-items-start gap-2">
                                                        <Form.Check
                                                            type="radio"
                                                            name="deliveryAddress"
                                                            checked={selectedAddressId === address.id}
                                                            readOnly
                                                        />
                                                        <div>
                                                            <div className="fw-bold fs-sm">
                                                                {address.label}
                                                                {address.is_default && (
                                                                    <Badge bg="primary" className="ms-2">Default</Badge>
                                                                )}
                                                            </div>
                                                            <div className="fs-sm text-secondary">
                                                                {[address.house_number, address.street, address.landmark]
                                                                    .filter(Boolean)
                                                                    .join(' ')}
                                                            </div>
                                                            {(address.recipient_name || address.phone_number) && (
                                                                <div className="fs-xs text-secondary">
                                                                    {[address.recipient_name, address.phone_number]
                                                                        .filter(Boolean)
                                                                        .join(' · ')}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            ))}
                                        </div>
                                    )}

                                    {showNewAddressForm && (
                                        <Card className="mb-3 p-3">
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Label</Form.Label>
                                                        <Form.Select
                                                            size="sm"
                                                            value={newAddress.label}
                                                            onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })}
                                                        >
                                                            <option>Home</option>
                                                            <option>Office</option>
                                                            <option>Other</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>House No.</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            placeholder="e.g. 12A"
                                                            value={newAddress.house_number}
                                                            onChange={(e) => setNewAddress({ ...newAddress, house_number: e.target.value })}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Street Address *</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    placeholder="e.g. Adeola Odeku Street"
                                                    value={newAddress.street}
                                                    onChange={(e) => setNewAddress({ ...newAddress, street: e.target.value })}
                                                />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Landmark</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    placeholder="e.g. opposite the filling station"
                                                    value={newAddress.landmark}
                                                    onChange={(e) => setNewAddress({ ...newAddress, landmark: e.target.value })}
                                                />
                                            </Form.Group>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Recipient Name</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            placeholder="Who should receive it?"
                                                            value={newAddress.recipient_name}
                                                            onChange={(e) => setNewAddress({ ...newAddress, recipient_name: e.target.value })}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Phone Number</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            placeholder="e.g. 08012345678"
                                                            value={newAddress.phone_number}
                                                            onChange={(e) => setNewAddress({ ...newAddress, phone_number: e.target.value })}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Form.Check
                                                type="checkbox"
                                                label="Save to my address book"
                                                className="fs-sm mb-1"
                                                checked={newAddress.save_to_profile}
                                                onChange={(e) => setNewAddress({ ...newAddress, save_to_profile: e.target.checked })}
                                            />
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                disabled={savingAddress}
                                                onClick={handleSaveNewAddress}
                                            >
                                                {savingAddress
                                                    ? "Saving..."
                                                    : newAddress.save_to_profile
                                                        ? "Save & Use Address"
                                                        : "Use This Address"}
                                            </Button>
                                        </Card>
                                    )}

                                    {!showNewAddressForm && selectedAddressId && (
                                        <>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Recipient Name</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    placeholder="Who should receive the delivery?"
                                                    value={recipientName}
                                                    onChange={(e) => setRecipientName(e.target.value)}
                                                />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Phone Number</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    placeholder="Contact phone for delivery"
                                                    value={recipientPhone}
                                                    onChange={(e) => setRecipientPhone(e.target.value)}
                                                />
                                            </Form.Group>
                                        </>
                                    )}

                                    {selectedAddress?.zone?.delivery_fee > 0 && (
                                        <div className="d-flex justify-content-between align-items-center fs-sm mb-2 px-2 py-1 rounded border bg-light">
                                            <span>Delivery Fee ({selectedAddress.zone.name})</span>
                                            <strong>₦{Number(selectedAddress.zone.delivery_fee).toLocaleString()}</strong>
                                        </div>
                                    )}

                                    <Form.Group className="mb-3">
                                        <Form.Label>Delivery Instructions (optional)</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            placeholder="e.g. call when you arrive at the gate"
                                            value={deliveryNote}
                                            onChange={(e) => setDeliveryNote(e.target.value)}
                                        />
                                    </Form.Group>
                                </>
                            )}
                        </Form>
                    )}
                    </>
                    )}
                </div>
                <div>
                    {/* {step === 1 && (
                    <Button variant="primary" onClick={() => setStep(2)} disabled={cartItems.length === 0}>
                        Continue
                    </Button>
                )} */}
                    {step === 2 && (
                        <>
                            <Button variant="secondary" onClick={() => setStep(1)}>
                                Back
                            </Button>
                            &nbsp;
                            <Button variant="primary" onClick={() => setStep(3)}>
                                Continue
                            </Button>
                        </>
                    )}
                    {!placedOrder && step === 3 && (
                        <>
                            <Button variant="secondary" onClick={() => setStep(1)} disabled={submitting}>
                                Back
                            </Button>
                            &nbsp;
                            <Button variant="primary" disabled={submitting}
                                onClick={() => {
                                    if (!deliveryMethod) {
                                        toast.error("Please select delivery method");
                                        return;
                                    }
                                    if (deliveryMethod === 'home') {
                                        if (!selectedAddressId) {
                                            toast.error("Please choose a saved address or add a new one");
                                            return;
                                        }
                                        const usingUnsaved = String(selectedAddressId).startsWith('unsaved-');
                                        if (usingUnsaved && !newAddress.street.trim()) {
                                            toast.error("Please enter the street address");
                                            return;
                                        }
                                    }
                                    handleSubmit();
                                }}>
                                {submitting ? "Processing..." : "Checkout"}
                            </Button>
                        </>
                    )}
                    {placedOrder && (
                        <>
                            <Button variant="secondary" disabled={submitting} onClick={payAtFacility}>
                                Pay at Facility
                            </Button>
                            &nbsp;
                            <Button variant="primary" disabled={submitting} onClick={handlePayNow}>
                                {submitting ? "Processing..." : "Pay Now"}
                            </Button>
                        </>
                    )}
                </div>
            </div >
            );
};

            export default DrugCartModal;
